import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { Box, Divider, Stack } from '@mui/material'
import { Sidebar, Menu, MenuItem, SubMenu } from 'react-pro-sidebar'
import { FaAngleDoubleLeft, FaAngleDoubleRight, FaCamera } from "react-icons/fa"
import { TbCertificate } from "react-icons/tb"
import { RiAdminFill } from "react-icons/ri"

import { GoAlertFill, GoLog } from "react-icons/go"
import { FaTools, FaLock, FaUsers } from "react-icons/fa"
import { MdOutlineAir, MdEditDocument, MdDashboard, MdDevices, MdLocalLibrary } from "react-icons/md"

import { GrUserWorker } from "react-icons/gr"
import { TbCarCrane } from "react-icons/tb"
import { RiLiveFill } from "react-icons/ri"
import { FaHouseFloodWater } from "react-icons/fa6"
import { TbDeviceWatchPin, TbDeviceWatchCode } from "react-icons/tb"
import { GrProjects } from "react-icons/gr"
import { LiaUserLockSolid } from "react-icons/lia"

import useAuth from 'Shared/Hooks/useAuth'

const MenuTab = ({ mode, open, setOpen, collapse, icon, route, label, subMenu }) => {
  const { organization, project } = useParams()

  const onMenuClick = () => {
    if (open === route) {
      setOpen()
    }
    else {
      setOpen(route)
    }
  }

  return (<>
    {subMenu ? (
      <SubMenu style={{ justifyContent: "center" }} label={!collapse && label} icon={icon} open={open === route} onOpenChange={onMenuClick}>
        {subMenu.map((i) => <>
          {(i != null) && 
            <MenuItem style={collapse ? { zIndex: 1000 } : { marginLeft: "10px", marginRight: "10px", backgroundColor: "#DCDCDC" }} component={<Link to={`/${mode}/${organization}/${project}/${route}/${i["key"]}`}/>} onClick={() => setOpen()}>
              {i["text"]} 
            </MenuItem> 
          }
        </>)}
      </SubMenu>
    ) : (
      <MenuItem  style={{ justifyContent: "center" }} icon={icon} component={<Link to={`/${mode}/${organization}${project ? `/${project}` : ""}/${route}`}/>}>
        {!collapse && label}
      </MenuItem>
    )}
  </>)
}

const DrawerMenu = ({ mode, collapse, setCollapse, windowSize, appbarHeight, drawerWidth, projectInfo }) => {
  const { t } = useTranslation()
  const { role } = useAuth()

  const [open, setOpen] = useState()

  const modules = (projectInfo && projectInfo["modules"].filter((i) => i["enabled"]).map((i) => i["module"])) ?? []
  
  const tabs = {
    "portal": {
      "general": {
        "label": "General",
        "always_show": true,
        "tabs": {
          "dashboard": { "text": t("module_dashboard"), "icon": <MdDashboard size={25}/>, "always_show": true, "admin_only": false, "subMenu": null},
          "alert": { "text": t("module_alerts"), "icon": <GoAlertFill size={25}/>, "always_show": true, "admin_only": false, "subMenu": null},
        }
      },

      "surveillance": {
        "label": "Surveillance",
        "always_show": false,
        "tabs": {
          "live": { "text": t("module_live"), "icon": <RiLiveFill size={25}/>, "always_show": true, "admin_only": false, "subMenu": null},
          "cameras": { "text": t("module_cameras"), "icon": <FaCamera size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_cameras_list")},
            (["admin"].includes(role)) ? {"key": "playback", "text": t("module_cameras_playback")} : null
          ]},
          "proximity": { "text": t("module_proximity"), "icon": <TbCarCrane size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_proximity_list")},
          ]}
        }
      },

      "digitalization": {
        "label": "Digitalization",
        "always_show": false,
        "tabs": {
          "permit_to_work": { "text": t("module_permitToWork"), "icon": <TbCertificate size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_permitToWork_list")},
            {"key": "templates", "text": t("module_permitToWork_templates")},
          ]},
          "assets_tracking": { "text": t("module_assetsTracking"), "icon": <FaTools size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_assetsTracking_list")},
            {"key": "types", "text": t("module_assetsTracking_types")},
          ]},
          "vr_training": { "text": t("module_vrTraining"), "icon": <MdEditDocument size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "records", "text": t("module_vrTraining_records")},
          ]}
        }
      },

      "smart_devices": {
        "label": "Smart Devices",
        "always_show": false,
        "tabs": {
          "outdoor_smartwatch": { "text": t("module_outdoorSmartwatch"), "icon": <TbDeviceWatchPin size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_outdoorSmartwatch_list")},
            (["pm", "so", "admin"].includes(role)) ? {"key": "devices", "text": t("module_outdoorSmartwatch_devices")} : null,
            (["admin"].includes(role)) ? {"key": "geofencing", "text": t("module_outdoorSmartwatch_geofencing")} : null,
            (["pm", "so", "admin"].includes(role)) ? {"key": "history", "text": t("module_outdoorSmartwatch_history")} : null
          ]},
          "confined_space_smartwatch": { "text": t("module_confinedSpaceSmartwatch"), "icon": <TbDeviceWatchCode size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_confinedSpaceSmartwatch_list")},
            (["pm", "so", "admin"].includes(role)) ? {"key": "devices", "text": t("module_confinedSpaceSmartwatch_devices")} : null,
            (["pm", "so", "admin"].includes(role)) ? {"key": "history", "text": t("module_confinedSpaceSmartwatch_history")} : null,
            // {"key": "zones", "text": t("module_confinedSpaceSmartwatch_zones")},
            // (["pm", "so", "admin"].includes(role)) ? {"key": "standby_person", "text": t("module_confinedSpaceSmartwatch_standbyPerson")} : null
          ]},
          "gas_detector": { "text": t("module_gasDetector"), "icon": <MdOutlineAir size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_gasDetector_data")},
          ]},
          "flood_sensor": { "text": t("module_floodingSensor"), "icon": <FaHouseFloodWater size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_floodingSensor_data")},
          ]},
          "smart_lock": { "text": t("module_smartlock"), "icon": <FaLock size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_smartlock_list")},
          ]}
        }
      },

      "access_management": {
        "label": "Access Management",
        "always_show": false,
        "tabs": {
          "access_control": { "text": t("module_accessControl"), "icon": <LiaUserLockSolid size={25}/>, "always_show": false, "admin_only": false, "subMenu": [
            {"key": "list", "text": t("module_accessControl_list")},
          ]}
        }
      },

      "project_management": {
        "label": "Project Management",
        "always_show": true,
        "tabs": {
          "confined_space": { "text": t("module_confinedSpace"), "icon": <MdLocalLibrary size={25}/>, "always_show": false, "modules": ["confined_space_smartwatch"], "admin_only": true, "subMenu": [
            {"key": "zones", "text": t("module_confinedSpaceSmartwatch_zones")},
            (["pm", "so", "admin"].includes(role)) ? {"key": "standby_person", "text": t("module_confinedSpaceSmartwatch_standbyPerson")} : null
          ]},
          "device_management": { "text": t("module_deviceManagement"), "icon": <MdDevices size={25}/>, "always_show": false, "modules": ["cameras"], "admin_only": true, "subMenu": [
            {"key": "network_router", "text": t("module_deviceManagement_router")},
          ]},
          "workers": { "text": t("module_workers"), "icon": <GrUserWorker size={25}/>, "always_show": true, "admin_only": false, "subMenu": null},
          "system_users": { "text": t("module_users"), "icon": <FaUsers size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
          "system_log": { "text": t("module_systemLog"), "icon": <GoLog size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
        }
      }
    },
    
    "admin_console": {
      "user_management": {
        "label": "User Management",
        "always_show": true,
        "tabs": {
          "system_admins": { "text": t("module_admins"), "icon": <RiAdminFill size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
          "system_users": { "text": t("module_users"), "icon": <FaUsers size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
          "projects": { "text": t("module_projects"), "icon": <GrProjects size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
          "system_log": { "text": t("module_systemLog"), "icon": <GoLog size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
        }
      },
      "device_management": {
        "label": t("module_deviceManagement"),
        "always_show": true,
        "tabs": {
          "network_router": { "text": t("module_deviceManagement_router"), "icon": <MdDevices size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
          "cameras": { "text": t("module_cameras"), "icon": <FaCamera size={25}/>, "always_show": true, "admin_only": true, "subMenu": null},
        }
      },
    }
  }

  return (
    <Box sx={{ display: 'block', width: collapse ? drawerWidth["xs"] : drawerWidth["lg"], height: "100%" }}>        
      <Sidebar collapsed={collapse} width={drawerWidth["lg"]} collapsedWidth={drawerWidth["xs"]}>
        <div style={{ height: windowSize["height"]-appbarHeight, display: "flex", flexDirection: "column" }}>
          <div style={{ overflow: "auto", height: "100%", flex: 1, backgroundColor: "white" }}>
            <Menu closeOnClick style={{ height: "100%" }}>
              <Stack>
                {Object.entries(tabs[mode]).filter(([k, v]) => (modules.some(item => Object.keys(v["tabs"]).includes(item)) || v["always_show"] === true)).map(([k, v]) =>
                  <div style={{ margin: "5px 0px 5px 0px" }}>
                    <div>
                      {!collapse && 
                        <div style={{ margin: "0px 0px 10px 15px" }}><b>{v["label"]}</b></div>
                      }
                    </div>

                    {Object.keys(v["tabs"]).map((i) => {
                      const info = tabs[mode][k]["tabs"][i]

                      if (info["always_show"] === false && !modules.includes(i) && !(info["modules"] != null && info["modules"].some(item => modules.includes(item)))) {
                        return <></>
                      }

                      if (role !== "admin" && info["admin_only"] === true) {
                        return <></>
                      }

                      return (
                        <MenuTab
                          mode={mode} route={i} icon={info["icon"]} label={info["text"]} subMenu={info["subMenu"]}
                          open={open} setOpen={setOpen} collapse={collapse}
                        />
                      )
                    })}
                  </div>
                )}
              </Stack>
            </Menu>
          </div>
          
          <div>
            <Divider sx={{ bgcolor: "secondary.light" }}/>

            <Menu style={{ backgroundColor: "white" }}>
              <MenuItem icon={collapse ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />} onClick={() => setCollapse(!collapse)} />
            </Menu>
          </div>
        </div>
      </Sidebar>
    </Box>
  )
}

export default DrawerMenu
